import { token } from '@bp-files/ui';
import { inkToStrokes, type SignatureInk } from './signatureInk';

/**
 * A stored mark, drawn into a box as SVG (USR-13, FIL-8c).
 *
 * **One renderer, shared.** `FieldLayer`'s placed stamp and `PdfViewer`'s
 * toolbar sign button both need to show the same ink at a different size —
 * this is the SVG half of the three renderers `inkToStrokes` feeds (the
 * others are `StoredSignature`'s canvas and `signPdf.ts`'s PDF vector ops).
 */
export function InkPreview({
  ink,
  width,
  height,
}: {
  ink: SignatureInk | undefined;
  width: number;
  height: number;
}) {
  if (ink === undefined || width <= 0 || height <= 0) return null;

  const drawn = Math.min(height, width / ink.aspect);
  const strokes = inkToStrokes(ink, drawn);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      aria-hidden="true"
      focusable="false"
    >
      <g
        transform={`translate(${String((width - drawn * ink.aspect) / 2)}, ${String((height - drawn) / 2)})`}
      >
        {strokes.map((stroke, strokeIndex) =>
          stroke.slice(1).map((to, index) => {
            const from = stroke[index];
            if (from === undefined) return null;

            return (
              <line
                key={`${String(strokeIndex)}-${String(index)}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={token('bp-ink')}
                strokeWidth={Math.max(0.5, (from.w + to.w) / 2)}
                strokeLinecap="round"
              />
            );
          }),
        )}
      </g>
    </svg>
  );
}
